import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Building2,
  Phone,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  MapPin,
  Globe,
  Mail,
  Wifi,
  CreditCard,
  ParkingCircle,
  Image,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Upload,
  ImagePlus,
  User,
  UserPlus,
  UserMinus
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { adminBusinessesApi, adminUsersApi } from '../../services/adminApi';

const businessCategories = [
  { value: 'restaurant', label: 'Restoran' },
  { value: 'cafe', label: 'Kafe' },
  { value: 'hotel', label: 'Otel' },
  { value: 'shopping', label: 'Alışveriş' },
  { value: 'market', label: 'Market' },
  { value: 'cinema', label: 'Sinema' },
  { value: 'theater', label: 'Tiyatro' },
  { value: 'health', label: 'Sağlık' },
  { value: 'beauty', label: 'Güzellik' },
  { value: 'sport', label: 'Spor' },
  { value: 'education', label: 'Eğitim' },
  { value: 'automotive', label: 'Otomotiv' },
  { value: 'service', label: 'Hizmet' },
  { value: 'finance', label: 'Finans' },
  { value: 'government', label: 'Kamu' },
  { value: 'bakery', label: 'Pastane' },
  { value: 'accommodation', label: 'Konaklama' }
];

const featureOptions = [
  { value: 'wifi', label: 'Kablosuz İnternet', icon: Wifi },
  { value: 'credit_card', label: 'Kredi Kartı', icon: CreditCard },
  { value: 'parking', label: 'Otopark', icon: ParkingCircle },
  { value: 'wheelchair', label: 'Engelli Erişimi', icon: CheckCircle },
];

const AdminBusinessesPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const mainImageInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  
  // Owner assignment states
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assigningOwner, setAssigningOwner] = useState(false);
  const [currentOwner, setCurrentOwner] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    short_description: '',
    image: '',
    gallery: [],
    address: '',
    phone: '',
    email: '',
    website: '',
    hours: '',
    rating: 4.5,
    review_count: 0,
    price_range: '$$',
    is_approved: false,
    is_verified: false,
    // Location
    maps_url: '',
    latitude: '',
    longitude: '',
    // Features
    features: [],
    tags: [],
    // Working hours
    working_hours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '21:00', closed: false },
    },
    // Social media
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: ''
  });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const data = await adminBusinessesApi.getAll({
        search,
        category: categoryFilter,
        status: statusFilter,
        page,
        limit: 20
      });
      setBusinesses(data.businesses);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for owner assignment
  const fetchUsers = async (searchTerm = '') => {
    setLoadingUsers(true);
    try {
      const response = await adminUsersApi.getAll({ search: searchTerm, limit: 20 });
      setUsers(response.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch current owner info
  const fetchOwnerInfo = async (ownerId) => {
    if (!ownerId) {
      setCurrentOwner(null);
      return;
    }
    try {
      const response = await adminUsersApi.getAll({ limit: 100 });
      const owner = response.users?.find(u => u.id === ownerId);
      setCurrentOwner(owner || null);
    } catch (error) {
      console.error('Error fetching owner:', error);
      setCurrentOwner(null);
    }
  };

  // Assign owner to business
  const handleAssignOwner = async (userId) => {
    if (!editingBusiness) return;
    setAssigningOwner(true);
    try {
      await adminBusinessesApi.assignOwner(editingBusiness.id, userId);
      alert('Sahip başarıyla atandı!');
      fetchBusinesses();
      const user = users.find(u => u.id === userId);
      setCurrentOwner(user);
    } catch (error) {
      console.error('Error assigning owner:', error);
      alert('Sahip atama başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAssigningOwner(false);
    }
  };

  // Remove owner from business
  const handleRemoveOwner = async () => {
    if (!editingBusiness || !confirm('Sahip kaldırılsın mı?')) return;
    setAssigningOwner(true);
    try {
      await adminBusinessesApi.removeOwner(editingBusiness.id);
      alert('Sahip kaldırıldı');
      setCurrentOwner(null);
      fetchBusinesses();
    } catch (error) {
      console.error('Error removing owner:', error);
      alert('Sahip kaldırma başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAssigningOwner(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, [search, categoryFilter, statusFilter, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      // Clean up formData - convert empty strings to null for optional fields
      const cleanedData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        rating: parseFloat(formData.rating) || 4.5,
        review_count: parseInt(formData.review_count) || 0,
        image: formData.image || null,
        hours: formData.hours || null,
        email: formData.email || null,
        website: formData.website || null,
        maps_url: formData.maps_url || null,
        short_description: formData.short_description || null,
        price_range: formData.price_range || '$$',
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        twitter: formData.twitter || null,
        youtube: formData.youtube || null,
      };
      
      if (editingBusiness) {
        await adminBusinessesApi.update(editingBusiness.id, cleanedData);
      } else {
        await adminBusinessesApi.create(cleanedData);
      }
      fetchBusinesses();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save business:', error);
      alert('Kaydetme başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Normalize category value to match select options
  const normalizeCategory = (category) => {
    if (!category) return '';
    const categoryMap = {
      'Restoran': 'restaurant',
      'Restaurant': 'restaurant',
      'restoran': 'restaurant',
      'Kafe': 'cafe',
      'Cafe': 'cafe',
      'kafe': 'cafe',
      'Otel': 'hotel',
      'Hotel': 'hotel',
      'otel': 'hotel',
      'Alışveriş': 'shopping',
      'Shopping': 'shopping',
      'alışveriş': 'shopping',
      'Market': 'market',
      'market': 'market',
      'Sağlık': 'health',
      'Health': 'health',
      'sağlık': 'health',
      'Güzellik': 'beauty',
      'Beauty': 'beauty',
      'güzellik': 'beauty',
      'Spor': 'sport',
      'Sport': 'sport',
      'spor': 'sport',
      'Eğitim': 'education',
      'Education': 'education',
      'eğitim': 'education',
      'Otomotiv': 'automotive',
      'Automotive': 'automotive',
      'otomotiv': 'automotive',
      'Finans': 'finance',
      'Finance': 'finance',
      'finans': 'finance',
      'Kamu': 'government',
      'Government': 'government',
      'kamu': 'government',
      'Pastane': 'bakery',
      'Bakery': 'bakery',
      'pastane': 'bakery',
      'Sinema': 'cinema',
      'Cinema': 'cinema',
      'sinema': 'cinema',
      'Tiyatro': 'theater',
      'Theater': 'theater',
      'tiyatro': 'theater',
      'Konaklama': 'accommodation',
      'Accommodation': 'accommodation',
      'konaklama': 'accommodation',
      'Hizmet': 'service',
      'Service': 'service',
      'hizmet': 'service'
    };
    return categoryMap[category] || category.toLowerCase();
  };

  const handleEdit = (business) => {
    setEditingBusiness(business);
    setActiveTab('basic');
    setFormData({
      name: business.name || '',
      category: normalizeCategory(business.category),
      description: business.description || '',
      short_description: business.short_description || '',
      image: business.image || '',
      gallery: business.gallery || [],
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      website: business.website || '',
      hours: business.hours || '',
      rating: business.rating || 4.5,
      review_count: business.review_count || business.reviewCount || 0,
      price_range: business.price_range || '$$',
      is_approved: business.is_approved !== false,
      is_verified: business.is_verified || false,
      // Location
      maps_url: business.maps_url || '',
      latitude: business.latitude || '',
      longitude: business.longitude || '',
      // Features
      features: business.features || [],
      tags: business.tags || [],
      // Working hours
      working_hours: business.working_hours || {
        monday: { open: '09:00', close: '22:00', closed: false },
        tuesday: { open: '09:00', close: '22:00', closed: false },
        wednesday: { open: '09:00', close: '22:00', closed: false },
        thursday: { open: '09:00', close: '22:00', closed: false },
        friday: { open: '09:00', close: '23:00', closed: false },
        saturday: { open: '10:00', close: '23:00', closed: false },
        sunday: { open: '10:00', close: '21:00', closed: false },
      },
      // Social media
      facebook: business.social_media?.facebook || '',
      instagram: business.social_media?.instagram || '',
      twitter: business.social_media?.twitter || '',
      youtube: business.social_media?.youtube || ''
    });
    // Load owner info
    fetchOwnerInfo(business.owner_id);
    setShowModal(true);
  };

  const handleApprove = async (id) => {
    try {
      await adminBusinessesApi.approve(id);
      fetchBusinesses();
    } catch (error) {
      console.error('Failed to approve business:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu işletmeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await adminBusinessesApi.delete(id);
      fetchBusinesses();
    } catch (error) {
      console.error('Failed to delete business:', error);
    }
  };

  const resetForm = () => {
    setEditingBusiness(null);
    setActiveTab('basic');
    setFormData({
      name: '',
      category: '',
      description: '',
      short_description: '',
      image: '',
      gallery: [],
      address: '',
      phone: '',
      email: '',
      website: '',
      hours: '',
      rating: 4.5,
      review_count: 0,
      price_range: '$$',
      is_approved: false,
      is_verified: false,
      maps_url: '',
      latitude: '',
      longitude: '',
      features: [],
      tags: [],
      working_hours: {
        monday: { open: '09:00', close: '22:00', closed: false },
        tuesday: { open: '09:00', close: '22:00', closed: false },
        wednesday: { open: '09:00', close: '22:00', closed: false },
        thursday: { open: '09:00', close: '22:00', closed: false },
        friday: { open: '09:00', close: '23:00', closed: false },
        saturday: { open: '10:00', close: '23:00', closed: false },
        sunday: { open: '10:00', close: '21:00', closed: false },
      },
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: ''
    });
  };

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  const toggleFeature = (feature) => {
    const newFeatures = formData.features.includes(feature)
      ? formData.features.filter(f => f !== feature)
      : [...formData.features, feature];
    setFormData({ ...formData, features: newFeatures });
  };

  // Upload image function
  const uploadImage = async (file, isGallery = false) => {
    const token = localStorage.getItem('admin_token');
    console.log('Upload started, token exists:', !!token);
    
    if (!token) {
      alert('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
      return null;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      console.log('Uploading to:', `${baseUrl}/api/admin/upload`);
      
      const response = await fetch(`${baseUrl}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Upload error response:', errorText);
        throw new Error(errorText || 'Yükleme başarısız');
      }

      const data = await response.json();
      console.log('Upload success:', data);
      
      // Construct full URL for display
      const fullUrl = data.url.startsWith('/') ? `${baseUrl}${data.url}` : data.url;
      return fullUrl;
    } catch (error) {
      console.error('Upload error:', error);
      alert('Dosya yüklenirken hata oluştu: ' + error.message);
      return null;
    }
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const url = await uploadImage(file);
    if (url) {
      setFormData({ ...formData, image: url });
    }
    setUploadingImage(false);
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    console.log('Gallery upload triggered, files:', files.length);
    if (files.length === 0) return;

    setUploadingGallery(true);
    const newUrls = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log('Uploading file:', file.name);
      const url = await uploadImage(file, true);
      console.log('Upload result:', url);
      if (url) {
        newUrls.push(url);
      }
    }

    console.log('New URLs:', newUrls);
    if (newUrls.length > 0) {
      const newGallery = [...(formData.gallery || []), ...newUrls];
      console.log('Setting new gallery:', newGallery);
      setFormData(prev => ({ 
        ...prev, 
        gallery: newGallery 
      }));
    }
    setUploadingGallery(false);
    
    // Reset file input
    e.target.value = '';
  };

  const removeGalleryImage = (indexToRemove) => {
    setFormData({
      ...formData,
      gallery: formData.gallery.filter((_, index) => index !== indexToRemove)
    });
  };

  const updateWorkingHour = (day, field, value) => {
    setFormData({
      ...formData,
      working_hours: {
        ...formData.working_hours,
        [day]: {
          ...formData.working_hours[day],
          [field]: value
        }
      }
    });
  };

  const getCategoryLabel = (value) => {
    return businessCategories.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">İşletmeler</h1>
          <p className="text-slate-400 mt-1">Toplam {total} işletme</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni İşletme
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            type="text"
            placeholder="İşletme ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="all">Tüm Kategoriler</option>
          {businessCategories.map((cat) => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="approved">Onaylı</option>
          <option value="pending">Onay Bekliyor</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase">İşletme</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase">Kategori</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase">Durum</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-400 uppercase">İletişim</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-slate-400 uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : businesses.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                    İşletme bulunamadı
                  </td>
                </tr>
              ) : (
                businesses.map((business) => (
                  <tr key={business.id} className="hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <img src={business.image} alt={business.name} className="w-12 h-12 rounded-lg object-cover" />
                        <div>
                          <p className="text-white font-medium">{business.name}</p>
                          <p className="text-slate-400 text-sm truncate max-w-[200px]">{business.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-slate-700 rounded-full text-xs text-slate-300">
                        {getCategoryLabel(business.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {business.is_approved !== false ? (
                        <span className="flex items-center gap-1 text-emerald-400 text-sm">
                          <CheckCircle className="w-4 h-4" /> Onaylı
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-orange-400 text-sm">
                          <XCircle className="w-4 h-4" /> Bekliyor
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-400">
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {business.phone || '-'}</p>
                        <p className="flex items-center gap-1"><Clock className="w-3 h-3" /> {business.hours || '-'}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!business.is_approved && (
                          <button
                            onClick={() => handleApprove(business.id)}
                            className="p-2 hover:bg-emerald-500/20 rounded-lg text-emerald-400 hover:text-emerald-300 transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(business)}
                          className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(business.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingBusiness ? 'İşletme Düzenle' : 'Yeni İşletme'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex border-b border-slate-700 px-6 overflow-x-auto">
              {[
                { id: 'basic', label: 'Temel Bilgiler' },
                { id: 'location', label: 'Konum' },
                { id: 'hours', label: 'Çalışma Saatleri' },
                { id: 'features', label: 'Özellikler' },
                { id: 'gallery', label: 'Galeri' },
                { id: 'social', label: 'Sosyal Medya' },
                ...(editingBusiness ? [{ id: 'owner', label: 'Sahip' }] : [])
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-slate-400 hover:text-slate-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
              {/* Temel Bilgiler Tab */}
              {activeTab === 'basic' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">İşletme Adı *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Kategori *</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        required
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      >
                        <option value="">Seçin</option>
                        {businessCategories.map((cat) => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Fiyat Aralığı</label>
                      <select
                        value={formData.price_range}
                        onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                      >
                        <option value="$">$ (Ekonomik)</option>
                        <option value="$$">$$ (Orta)</option>
                        <option value="$$$">$$$ (Pahalı)</option>
                        <option value="$$$$">$$$$ (Lüks)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Kısa Açıklama</label>
                    <Input
                      value={formData.short_description}
                      onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                      placeholder="Örn: Lezzetli yemekleri ile ünlü"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Açıklama *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      rows={4}
                      className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Ana Görsel URL *</label>
                    <Input
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      required
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Telefon *</label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">E-posta</label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Website</label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://www.example.com"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="approved"
                        checked={formData.is_approved}
                        onChange={(e) => setFormData({ ...formData, is_approved: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500"
                      />
                      <label htmlFor="approved" className="text-slate-300">Onaylı</label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="verified"
                        checked={formData.is_verified}
                        onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500"
                      />
                      <label htmlFor="verified" className="text-slate-300 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                        Doğrulanmış İşletme
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Konum Tab */}
              {activeTab === 'location' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Adres *</label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Google Haritalar Linki
                      <span className="text-emerald-400 ml-2 text-xs">(Önerilen)</span>
                    </label>
                    <Input
                      value={formData.maps_url}
                      onChange={(e) => setFormData({ ...formData, maps_url: e.target.value })}
                      placeholder="https://maps.app.goo.gl/..."
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Google Haritalar'da konumu açın → Paylaş → Bağlantıyı kopyala
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Enlem</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.latitude}
                        onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Boylam</label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={formData.longitude}
                        onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Çalışma Saatleri Tab */}
              {activeTab === 'hours' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Genel Çalışma Saatleri</label>
                    <Input
                      value={formData.hours}
                      onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                      placeholder="09:00-22:00"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="border-t border-slate-700 pt-4">
                    <h4 className="text-sm font-medium text-slate-300 mb-3">Günlük Çalışma Saatleri</h4>
                    <div className="space-y-3">
                      {Object.entries(dayNames).map(([day, label]) => (
                        <div key={day} className="flex items-center gap-3">
                          <div className="w-24 text-slate-400 text-sm">{label}</div>
                          <input
                            type="checkbox"
                            checked={!formData.working_hours[day]?.closed}
                            onChange={(e) => updateWorkingHour(day, 'closed', !e.target.checked)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-900"
                          />
                          <Input
                            type="time"
                            value={formData.working_hours[day]?.open || '09:00'}
                            onChange={(e) => updateWorkingHour(day, 'open', e.target.value)}
                            disabled={formData.working_hours[day]?.closed}
                            className="w-28 bg-slate-900 border-slate-600 text-white text-sm"
                          />
                          <span className="text-slate-500">-</span>
                          <Input
                            type="time"
                            value={formData.working_hours[day]?.close || '22:00'}
                            onChange={(e) => updateWorkingHour(day, 'close', e.target.value)}
                            disabled={formData.working_hours[day]?.closed}
                            className="w-28 bg-slate-900 border-slate-600 text-white text-sm"
                          />
                          {formData.working_hours[day]?.closed && (
                            <span className="text-red-400 text-sm">Kapalı</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Özellikler Tab */}
              {activeTab === 'features' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-3">İşletme Özellikleri</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {featureOptions.map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => toggleFeature(value)}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            formData.features.includes(value)
                              ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                              : 'border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-sm">{label}</span>
                          {formData.features.includes(value) && (
                            <CheckCircle className="w-4 h-4 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Etiketler</label>
                    <Input
                      value={formData.tags?.join(', ') || ''}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                      placeholder="Örn: deniz manzarası, canlı müzik, aile dostu"
                      className="bg-slate-900 border-slate-600 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Virgülle ayırarak yazın</p>
                  </div>
                </div>
              )}

              {/* Galeri Tab */}
              {activeTab === 'gallery' && (
                <div className="space-y-6">
                  {/* Ana Görsel */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">Ana Görsel</label>
                    <div className="flex items-start gap-4">
                      {formData.image ? (
                        <div className="relative w-40 h-40 rounded-xl overflow-hidden">
                          <img src={formData.image} alt="Ana görsel" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, image: '' })}
                            className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-40 h-40 bg-slate-700 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-600">
                          <Image className="w-10 h-10 text-slate-500" />
                        </div>
                      )}
                      <div className="flex-1 space-y-3">
                        <input
                          type="file"
                          ref={mainImageInputRef}
                          onChange={handleMainImageUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => mainImageInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {uploadingImage ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Upload className="w-5 h-5" />
                          )}
                          {uploadingImage ? 'Yükleniyor...' : 'Fotoğraf Yükle'}
                        </button>
                        <div className="text-center text-slate-500 text-sm">veya</div>
                        <Input
                          value={formData.image}
                          onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                          placeholder="Görsel URL yapıştır"
                          className="bg-slate-900 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Galeri Görselleri */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-3">
                      Galeri Görselleri
                      <span className="text-slate-500 font-normal ml-2">({formData.gallery?.length || 0} fotoğraf)</span>
                    </label>
                    
                    <div className="grid grid-cols-4 gap-3 mb-4">
                      {formData.gallery?.map((img, index) => (
                        <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                          <img src={img} alt={`Galeri ${index + 1}`} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add More Button */}
                      <input
                        type="file"
                        ref={galleryInputRef}
                        onChange={handleGalleryUpload}
                        accept="image/*"
                        multiple
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => galleryInputRef.current?.click()}
                        disabled={uploadingGallery}
                        className="aspect-square rounded-xl border-2 border-dashed border-slate-600 flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-emerald-400 hover:border-emerald-500 transition-colors disabled:opacity-50"
                      >
                        {uploadingGallery ? (
                          <Loader2 className="w-8 h-8 animate-spin" />
                        ) : (
                          <ImagePlus className="w-8 h-8" />
                        )}
                        <span className="text-xs">{uploadingGallery ? 'Yükleniyor...' : 'Dosya Ekle'}</span>
                      </button>
                    </div>

                    {/* URL ile ekleme */}
                    <div className="flex gap-2 mb-4">
                      <Input
                        id="galleryUrlInput"
                        placeholder="Görsel URL yapıştır ve Ekle'ye tıkla"
                        className="flex-1 bg-slate-900 border-slate-600 text-white"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target;
                            if (input.value.trim()) {
                              setFormData({ 
                                ...formData, 
                                gallery: [...(formData.gallery || []), input.value.trim()] 
                              });
                              input.value = '';
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('galleryUrlInput');
                          if (input && input.value.trim()) {
                            setFormData({ 
                              ...formData, 
                              gallery: [...(formData.gallery || []), input.value.trim()] 
                            });
                            input.value = '';
                          }
                        }}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        URL Ekle
                      </button>
                    </div>

                    <p className="text-xs text-slate-500">
                      Dosya yükleyebilir veya URL yapıştırabilirsiniz. JPEG, PNG, GIF, WEBP desteklenir.
                    </p>
                  </div>
                </div>
              )}

              {/* Sosyal Medya Tab */}
              {activeTab === 'social' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <Facebook className="w-5 h-5 text-white" />
                    </div>
                    <Input
                      value={formData.facebook}
                      onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                      placeholder="Facebook profil URL"
                      className="flex-1 bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-500 rounded-lg flex items-center justify-center">
                      <Instagram className="w-5 h-5 text-white" />
                    </div>
                    <Input
                      value={formData.instagram}
                      onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                      placeholder="Instagram profil URL"
                      className="flex-1 bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-500 rounded-lg flex items-center justify-center">
                      <Twitter className="w-5 h-5 text-white" />
                    </div>
                    <Input
                      value={formData.twitter}
                      onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                      placeholder="Twitter profil URL"
                      className="flex-1 bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <Youtube className="w-5 h-5 text-white" />
                    </div>
                    <Input
                      value={formData.youtube}
                      onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                      placeholder="YouTube kanal URL"
                      className="flex-1 bg-slate-900 border-slate-600 text-white"
                    />
                  </div>
                </div>
              )}

              {/* Sahip Tab */}
              {activeTab === 'owner' && editingBusiness && (
                <div className="space-y-6">
                  {/* Current Owner */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">Mevcut Sahip</h3>
                    {currentOwner ? (
                      <div className="flex items-center justify-between p-4 bg-slate-700 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{currentOwner.name}</p>
                            <p className="text-slate-400 text-sm">{currentOwner.email}</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={handleRemoveOwner}
                          disabled={assigningOwner}
                          className="bg-red-500 hover:bg-red-600"
                        >
                          {assigningOwner ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4 mr-2" />}
                          Kaldır
                        </Button>
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                        <User className="w-12 h-12 text-slate-500 mx-auto mb-2" />
                        <p className="text-slate-400">Bu işletmenin henüz bir sahibi yok</p>
                      </div>
                    )}
                  </div>

                  {/* Search Users */}
                  {!currentOwner && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Sahip Ata</h3>
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          value={userSearch}
                          onChange={(e) => {
                            setUserSearch(e.target.value);
                            fetchUsers(e.target.value);
                          }}
                          onFocus={() => fetchUsers(userSearch)}
                          placeholder="Kullanıcı ara (isim veya email)"
                          className="pl-10 bg-slate-900 border-slate-600 text-white"
                        />
                      </div>

                      {/* Users List */}
                      {loadingUsers ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                        </div>
                      ) : users.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {users.filter(u => u.role !== 'business_owner').map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center justify-between p-3 bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-slate-300" />
                                </div>
                                <div>
                                  <p className="text-white text-sm font-medium">{user.name}</p>
                                  <p className="text-slate-400 text-xs">{user.email}</p>
                                </div>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleAssignOwner(user.id)}
                                disabled={assigningOwner}
                                className="bg-emerald-500 hover:bg-emerald-600"
                              >
                                {assigningOwner ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                                Ata
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : userSearch ? (
                        <p className="text-slate-400 text-center py-4">Kullanıcı bulunamadı</p>
                      ) : (
                        <p className="text-slate-400 text-center py-4">Kullanıcı aramak için yukarıya yazın</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-slate-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  disabled={actionLoading}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Kaydet'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBusinessesPage;
